import { formatCurrency, formatPercent, formatWaDtiBps } from 'configs/columns';
import { BaseFiltersConfig } from 'features/common-elements';
import {
  AssetClassNavEntry,
  useEnabledAssetClassNavEntries,
} from 'features/pages/portfolio/Portfolio/SideNav/SideNav.config';
import { invert } from 'lodash';
import { useMemo } from 'react';
import { ASSET_CLASS_IDS, marketplaceAssetClassLabel } from 'types/assetClass';

import { AssetClass } from '__generated__/globalTypes';
export type ListingColConfig<K extends keyof T, T = ListingCols> = {
  string: string;
  numeric: boolean;
  sortSelector?: K;
  display: (value: T[K]) => string | JSX.Element;
};
export type ListingColsConfig<T = ListingCols> = {
  [K in keyof T]: ListingColConfig<K, T>;
};

export interface ListingCols {
  name: string | null;
  t_current_balance_cents: Long | null;
  asset_class: AssetClass;
  loan_count: number | null;
  wa_borrower_credit_score: number | null;
  wa_coupon_bps: number | null;
  wa_dti_bps: number | null;
  wa_loan_age_months: number | null;
  wa_remaining_loan_terms_months: number | null;
  wa_ltv_bps: number | null;
}

export const listingColsConfig: ListingColsConfig = {
  name: {
    string: 'Name',
    numeric: false,
    sortSelector: 'name',
    display: (value: string | null) => value ?? '-',
  },
  t_current_balance_cents: {
    string: 'UPB',
    numeric: true,
    sortSelector: 't_current_balance_cents',
    display: (cents: string | number | Record<string, unknown> | null) =>
      formatCurrency(cents) ?? '-',
  },
  asset_class: {
    string: 'Asset Class',
    numeric: false,
    sortSelector: 'asset_class',
    display: (value: string | JSX.Element) => {
      if (!value) {
        return '-';
      }
      if (typeof value !== 'string') {
        return value;
      }

      return marketplaceAssetClassLabel(value);
    },
  },
  loan_count: {
    string: '# LOANS',
    numeric: true,
    sortSelector: 'loan_count',
    display: (value: number | null) => value?.toLocaleString() ?? '-',
  },
  wa_loan_age_months: {
    string: 'WALA',
    numeric: true,
    sortSelector: 'wa_loan_age_months',
    display: (value: number | null) => value?.toLocaleString() ?? '-',
  },
  wa_remaining_loan_terms_months: {
    string: 'WAM',
    numeric: true,
    sortSelector: 'wa_remaining_loan_terms_months',
    display: (value: number | null) => value?.toLocaleString() ?? '-',
  },
  wa_coupon_bps: {
    string: 'GWAC',
    numeric: true,
    sortSelector: 'wa_coupon_bps',
    display: (bps: number | null) => formatPercent(bps ?? 0, 3) ?? '-',
  },
  wa_borrower_credit_score: {
    string: 'FICO',
    numeric: true,
    sortSelector: 'wa_borrower_credit_score',
    display: (value: number | null) => value?.toString() ?? '-',
  },
  wa_ltv_bps: {
    string: 'LTV',
    numeric: true,
    sortSelector: 'wa_ltv_bps',
    display: (bps: number | null) => (bps ? `${((bps ?? 0) / 100).toFixed(2)}%` : '-'),
  },
  wa_dti_bps: {
    string: 'DTI',
    numeric: true,
    sortSelector: 'wa_dti_bps',
    display: formatWaDtiBps,
  },
};

export type ListingKey = keyof typeof listingColsConfig;

export const listingColsOrder: ListingKey[] = [
  'name',
  'asset_class',
  't_current_balance_cents',
  'loan_count',
  'wa_loan_age_months',
  'wa_remaining_loan_terms_months',
  'wa_coupon_bps',
  'wa_borrower_credit_score',
  'wa_ltv_bps',
  'wa_dti_bps',
];

type AssetClassOption = { value: string; label: string };

const assetClassFilterOptions = (entries: AssetClassNavEntry[]): AssetClassOption[] =>
  entries
    .filter((entry) => ASSET_CLASS_IDS[entry.routeSegment] != null)
    .map((entry) => ({
      value: ASSET_CLASS_IDS[entry.routeSegment] as string,
      label: entry.text,
    }));

export const marketplaceFiltersConfig = {
  has_cra: {
    type: 'boolean',
    displayName: 'CRA',
    falseyLabel: 'Without CRA',
    truthyLabel: 'With CRA',
  },
  t_current_balance_cents: {
    type: 'range',
    displayName: `total ${listingColsConfig.t_current_balance_cents.string} (M)`,
    min: 0,
    max: 1000,
    prepare: (dollars: number) => dollars * 100 * 1000000,
  },
  wa_borrower_credit_score: {
    type: 'range',
    displayName: listingColsConfig.wa_borrower_credit_score.string,
    min: 300,
    max: 850,
    integer: true,
  },
  wa_coupon_bps: {
    type: 'range',
    displayName: listingColsConfig.wa_coupon_bps.string,
    min: 0,
    max: 25,
    prepare: (pct: number) => pct * 100,
  },
  wa_remaining_loan_terms_months: {
    type: 'range',
    displayName: listingColsConfig.wa_remaining_loan_terms_months.string,
    min: 0,
    max: 360,
  },
  wa_loan_age_months: {
    type: 'range',
    displayName: listingColsConfig.wa_loan_age_months.string,
    min: 0,
    max: 360,
  },
  wa_ltv_bps: {
    type: 'range',
    displayName: listingColsConfig.wa_ltv_bps.string,
    min: 0,
    max: 150,
    decimal: true,
    percentage: true,
    prepare: (pct: number) => pct * 100,
  },
  wa_dti_bps: {
    type: 'range',
    displayName: listingColsConfig.wa_dti_bps.string,
    min: 0,
    max: 150,
    decimal: true,
    percentage: true,
    prepare: (pct: number) => pct * 100,
  },
  asset_class: {
    type: 'select-single',
    displayName: listingColsConfig.asset_class.string,
    // Deliberately empty. The options depend on runtime flags, which a
    // module-level const cannot read; this value exists only so its *type*
    // drives filterKeyToAbbr and the FilterValue generics. Rendering this
    // directly gives an obviously-empty dropdown, where a plausible-looking
    // partial list would just be ENG-3414 again one call site over — go
    // through useMarketplaceFiltersConfig().
    filterOptions: [] as AssetClassOption[],
    getOptionLabel: (option) => (typeof option === 'string' ? option : option.label),
    getOptionValue: (option) => (typeof option === 'string' ? option : option.value),
    valueToString: (option) => (typeof option === 'string' ? option : (option?.label ?? '')),
  },
} as const satisfies BaseFiltersConfig;

export type MarketplaceFiltersConfig = typeof marketplaceFiltersConfig;

// The asset-class options depend on runtime feature flags, which a module-level
// const can't read. Anything that renders the marketplace filters must go
// through this hook, or flag-gated classes (MARINE, HELOC) stay unselectable.
export const useMarketplaceFiltersConfig = (): MarketplaceFiltersConfig => {
  const navEntries = useEnabledAssetClassNavEntries();

  return useMemo(
    () => ({
      ...marketplaceFiltersConfig,
      asset_class: {
        ...marketplaceFiltersConfig.asset_class,
        filterOptions: assetClassFilterOptions(navEntries),
      },
    }),
    [navEntries],
  );
};

export const filterKeyToAbbr: {
  [key in keyof typeof marketplaceFiltersConfig]: string;
} = {
  t_current_balance_cents: 'UPB',
  wa_borrower_credit_score: 'FICO',
  wa_coupon_bps: 'GWAC',
  wa_remaining_loan_terms_months: 'WAM',
  wa_loan_age_months: 'WALA',
  wa_ltv_bps: 'LTV',
  wa_dti_bps: 'DTI',
  asset_class: 'AC',
  has_cra: 'CRA',
  // servicing_retained: 'SR',
};

export const abbrToFilterKey = invert(filterKeyToAbbr) as {
  [key: string]: keyof typeof marketplaceFiltersConfig;
};
